<template>
  <div class="column content-box">
    <woot-modal-header
      :header-title="$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.TITLE')"
    />
    <form class="row" @submit.prevent="editWebhook">
      <div class="small-12 columns">
        <label :class="{ error: $v.endPoint.$error }">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.FORM.END_POINT.LABEL') }}
          <input
            v-model.trim="endPoint"
            type="text"
            name="endPoint"
            :placeholder="
              $t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.FORM.END_POINT.PLACEHOLDER')
            "
            @input="$v.endPoint.$touch"
          />
          <span v-if="$v.endPoint.$error" class="message">
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.FORM.END_POINT.ERROR') }}
          </span>
        </label>

        <label>
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.WEBHOOK_TYPE.LABEL') }}
        </label>
        <select v-model="webhookType" @change="onWebhookTypeChange">
          <option
            v-for="type in webhookTypes"
            :key="type.value"
            :value="type.value"
          >
            {{ type.label }}
          </option>
        </select>

        <label
          :class="{
            disabled: !isInboxType,
            error: $v.inboxId.$error,
          }"
        >
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.INBOX_ID.LABEL') }}
          <select
            v-model="inboxId"
            :disabled="!isInboxType"
            @change="$v.inboxId.$touch"
          >
            <option value="">
              {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.INBOX_ID.PLACEHOLDER') }}
            </option>
            <option
              v-for="inbox in filteredInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <span v-if="$v.inboxId.$error" class="message">
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.INBOX_ID.ERROR') }}
          </span>
        </label>

        <div :class="{ error: $v.events.$error }">
          <label>
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.EVENTS.LABEL') }}
          </label>
          <label v-for="event in availableEvents" :key="event.value">
            <input
              v-model="events"
              type="checkbox"
              :value="event.value"
              :checked="url && events.includes(event.value)"
              @change="$v.events.$touch"
            />
            {{ event.label }}
          </label>
          <span
            v-if="$v.events.$error"
            class="message"
            style="margin-top: 0; display: block"
          >
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.EVENTS.ERROR') }}
          </span>
        </div>
      </div>

      <div class="modal-footer">
        <div class="small-12 columns">
          <woot-button
            :is-disabled="isSubmitDisabled"
            :is-loading="uiFlags.updatingItem"
          >
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.FORM.SUBMIT') }}
          </woot-button>
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { required, url, minLength, requiredIf } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';
import {
  INTEGRATION_EVENTS,
  WEBHOOK_TYPES,
  ACCOUNT_TYPE_EVENTS,
  INBOX_TYPE_EVENTS,
} from './constants';

export default {
  mixins: [alertMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
    selectedEvents: {
      type: Array,
      required: true,
    },
    selectedWebhookType: {
      type: String,
      default: 'account_type',
    },
    selectedInboxId: {
      type: [Number, String],
      default: '',
    },
  },
  data() {
    return {
      alertMessage: '',
      endPoint: this.url,
      webhookId: this.id,
      events: this.selectedEvents,
      webhookType: this.selectedWebhookType || 'account_type',
      inboxId: this.selectedInboxId || '',
      webhookTypes: WEBHOOK_TYPES.map(type => ({
        value: type.value,
        label: this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.ADD.WEBHOOK_TYPE.${type.label}`
        ),
      })),
      integrationEvents: INTEGRATION_EVENTS.map(event => ({
        value: event.value,
        label: this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.ADD.EVENTS.${event.label}`
        ),
      })),
    };
  },
  validations: {
    inboxId: {
      requiredIf: requiredIf(function isInboxTypeSelected() {
        return this.isInboxType;
      }),
    },
    events: {
      required,
    },
    endPoint: {
      required,
      minLength: minLength(7),
      url,
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'webhooks/getUIFlags',
      inboxes: 'inboxes/getInboxes',
    }),
    isInboxType() {
      return this.webhookType === 'inbox_type';
    },
    availableEvents() {
      return this.integrationEvents.filter(event => {
        if (this.isInboxType) {
          return INBOX_TYPE_EVENTS.includes(event.value);
        }
        return ACCOUNT_TYPE_EVENTS.includes(event.value);
      });
    },
    filteredInboxes() {
      return this.inboxes.filter(inbox => inbox.chat_supported);
    },
    isSubmitDisabled() {
      return this.uiFlags.updatingItem;
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
  },
  methods: {
    resetForm() {
      this.endPoint = '';
      this.$v.endPoint.$reset();
    },
    onWebhookTypeChange() {
      this.events = [];
      this.inboxId = '';
    },
    async editWebhook() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        await this.$store.dispatch('webhooks/update', {
          webhook: {
            url: this.endPoint,
            subscriptions: this.events,
            webhook_type: this.webhookType,
            inbox_id: this.isInboxType ? this.inboxId : null,
          },
          id: this.webhookId,
        });
        this.alertMessage = this.$t(
          'INTEGRATION_SETTINGS.WEBHOOK.EDIT.API.SUCCESS_MESSAGE'
        );
        this.resetForm();
        this.onClose();
      } catch (error) {
        this.alertMessage =
          error.response.data.message ||
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.API.ERROR_MESSAGE');
      } finally {
        this.showAlert(this.alertMessage);
      }
    },
  },
};
</script>
